/**
 * Geometry math for the command engine (waves Z/A/E/L/G/H).
 * Pure functions over serializable point payloads; Z-up world, meters.
 * No React, no stores — command runners and entity renderers share this.
 */

import * as THREE from 'three';

export type Pt3 = [number, number, number];

// ============================================================================
// basic 2D (XY plane) helpers
// ============================================================================

export const xy = (p: Pt3) => new THREE.Vector2(p[0], p[1]);

export function polylineLength(points: Pt3[]): number {
    let sum = 0;
    for (let i = 1; i < points.length; i++) {
        sum += Math.hypot(points[i][0] - points[i - 1][0], points[i][1] - points[i - 1][1], points[i][2] - points[i - 1][2]);
    }
    return sum;
}

/** Shoelace area of a planar polygon (XY projection), absolute value. */
export function polygonArea(points: Pt3[]): number {
    let sum = 0;
    for (let i = 0; i < points.length; i++) {
        const a = points[i], b = points[(i + 1) % points.length];
        sum += a[0] * b[1] - b[0] * a[1];
    }
    return Math.abs(sum) / 2;
}

export function polygonPerimeter(points: Pt3[], closed = true): number {
    const pts = closed && points.length > 2 ? [...points, points[0]] : points;
    return polylineLength(pts);
}

export function centroid(points: Pt3[]): Pt3 {
    const c: Pt3 = [0, 0, 0];
    points.forEach((p) => { c[0] += p[0]; c[1] += p[1]; c[2] += p[2]; });
    const n = Math.max(points.length, 1);
    return [c[0] / n, c[1] / n, c[2] / n];
}

/** Sample a circle/arc into points on the XY plane at level z. */
export function arcPoints(cx: number, cy: number, r: number, a0: number, a1: number, z = 0, segments = 48): Pt3[] {
    const pts: Pt3[] = [];
    let sweep = a1 - a0;
    while (sweep <= 0) sweep += Math.PI * 2;
    const n = Math.max(8, Math.round((sweep / (Math.PI * 2)) * segments));
    for (let i = 0; i <= n; i++) {
        const a = a0 + (sweep * i) / n;
        pts.push([cx + Math.cos(a) * r, cy + Math.sin(a) * r, z]);
    }
    return pts;
}

export function circlePoints(cx: number, cy: number, r: number, z = 0, segments = 64): Pt3[] {
    return arcPoints(cx, cy, r, 0, Math.PI * 2, z, segments);
}

export function ellipsePoints(cx: number, cy: number, ra: number, rb: number, rot = 0, z = 0, segments = 64): Pt3[] {
    const pts: Pt3[] = [];
    for (let i = 0; i <= segments; i++) {
        const t = (i / segments) * Math.PI * 2;
        const x = ra * Math.cos(t);
        const y = rb * Math.sin(t);
        pts.push([cx + x * Math.cos(rot) - y * Math.sin(rot), cy + x * Math.sin(rot) + y * Math.cos(rot), z]);
    }
    return pts;
}

export function helixPoints(axisStart: Pt3, axisEnd: Pt3, radius: number, turns: number, segmentsPerTurn = 48): Pt3[] {
    const a = new THREE.Vector3(...axisStart);
    const b = new THREE.Vector3(...axisEnd);
    const axis = b.clone().sub(a);
    const len = axis.length();
    if (len < 1e-9) return [];
    axis.normalize();
    // Build a frame perpendicular to the axis.
    const tmp = Math.abs(axis.z) < 0.9 ? new THREE.Vector3(0, 0, 1) : new THREE.Vector3(1, 0, 0);
    const u = new THREE.Vector3().crossVectors(axis, tmp).normalize();
    const v = new THREE.Vector3().crossVectors(axis, u).normalize();
    const n = Math.max(16, Math.round(turns * segmentsPerTurn));
    const pts: Pt3[] = [];
    for (let i = 0; i <= n; i++) {
        const t = i / n;
        const ang = t * turns * Math.PI * 2;
        const p = a.clone()
            .addScaledVector(axis, t * len)
            .addScaledVector(u, Math.cos(ang) * radius)
            .addScaledVector(v, Math.sin(ang) * radius);
        pts.push([p.x, p.y, p.z]);
    }
    return pts;
}

/** Logarithmic/archimedean spiral in XY starting at center. */
export function spiralPoints(cx: number, cy: number, rStart: number, rEnd: number, turns: number, z = 0, segmentsPerTurn = 48): Pt3[] {
    const n = Math.max(16, Math.round(turns * segmentsPerTurn));
    const pts: Pt3[] = [];
    for (let i = 0; i <= n; i++) {
        const t = i / n;
        const r = rStart + (rEnd - rStart) * t;
        const a = t * turns * Math.PI * 2;
        pts.push([cx + Math.cos(a) * r, cy + Math.sin(a) * r, z]);
    }
    return pts;
}

/** Uniform resample of a polyline to fixed spacing (for arrays/points on curve). */
export function resamplePolyline(points: Pt3[], spacing: number): Pt3[] {
    if (points.length < 2 || spacing <= 0) return [];
    const out: Pt3[] = [[...points[0]]];
    let acc = 0;
    for (let i = 1; i < points.length; i++) {
        const a = new THREE.Vector3(...points[i - 1]);
        const b = new THREE.Vector3(...points[i]);
        const seg = a.distanceTo(b);
        let t = (spacing - acc) / seg;
        while (t <= 1) {
            const p = a.clone().lerp(b, t);
            out.push([p.x, p.y, p.z]);
            t += spacing / seg;
        }
        acc = (acc + seg) % spacing;
    }
    return out;
}

export interface Segment2 { a: Pt3; b: Pt3 }

/** Even-odd point-in-polygon test in XY (shared by clip consumers). */
export function pointInPolygon2(p: Pt3, poly: Pt3[]): boolean {
    let inside = false;
    for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
        const xi = poly[i][0], yi = poly[i][1], xj = poly[j][0], yj = poly[j][1];
        const hit = ((yi > p[1]) !== (yj > p[1]))
            && (p[0] < ((xj - xi) * (p[1] - yi)) / (yj - yi + 1e-12) + xi);
        if (hit) inside = !inside;
    }
    return inside;
}

/**
 * Expand a bulge segment (classic DXF LWPOLYLINE convention) into arc
 * points: bulge = tan(includedAngle/4), signed, 0 = straight chord.
 */
export function bulgeArcPoints(a: Pt3, b: Pt3, bulge: number, segments = 24): Pt3[] {
    if (!bulge || Math.abs(bulge) < 1e-10) return [a, b];
    const theta = 4 * Math.atan(bulge); // included angle, signed
    const chord = new THREE.Vector2(b[0] - a[0], b[1] - a[1]);
    const cLen = chord.length();
    if (cLen < 1e-9) return [a, b];
    const r = cLen / (2 * Math.sin(Math.abs(theta) / 2));
    // Center sits on the perpendicular bisector; sign of bulge picks the side.
    const mid = new THREE.Vector2((a[0] + b[0]) / 2, (a[1] + b[1]) / 2);
    const perp = new THREE.Vector2(-chord.y / cLen, chord.x / cLen);
    const d = r * Math.cos(theta / 2);
    const center = mid.clone().addScaledVector(perp, bulge > 0 ? -d : d);
    const a0 = Math.atan2(a[1] - center.y, a[0] - center.x);
    const out: Pt3[] = [];
    const n = Math.max(4, Math.round(Math.abs(theta) / (Math.PI * 2) * segments));
    for (let i = 0; i <= n; i++) {
        const t = a0 + (theta * i) / n;
        out.push([center.x + Math.cos(t) * r, center.y + Math.sin(t) * r, a[2] + ((b[2] ?? 0) - (a[2] ?? 0)) * (i / n)] as Pt3);
    }
    return out;
}

/** Expand a vertex chain with per-segment bulges into a dense polyline. */
export function expandBulgePolyline(vertices: Pt3[], bulges: number[] | undefined, closed: boolean): Pt3[] {
    if (!bulges || bulges.every((b) => !b)) return closed && vertices.length > 2 ? [...vertices, [...vertices[0]] as Pt3] : vertices;
    const segCount = closed ? vertices.length : Math.max(0, vertices.length - 1);
    const out: Pt3[] = [vertices[0]];
    for (let i = 0; i < segCount; i++) {
        const a = vertices[i];
        const b = vertices[(i + 1) % vertices.length];
        const arc = bulgeArcPoints(a, b, bulges[i] ?? 0);
        // skip the duplicated start point of each arc
        for (let k = 1; k < arc.length; k++) out.push(arc[k]);
    }
    return out;
}

/**
 * Solve the DXF bulge for a chord a→b that passes through a point m on the
 * arc: circumcircle sweep angle θ (signed CCW+), bulge = tan(θ/4).
 */
export function bulgeFrom3Points(a: Pt3, m: Pt3, b: Pt3): number {
    const d = 2 * (a[0] * (m[1] - b[1]) + m[0] * (b[1] - a[1]) + b[0] * (a[1] - m[1]));
    if (Math.abs(d) < 1e-9) return 0; // collinear
    const ux = ((a[0] ** 2 + a[1] ** 2) * (m[1] - b[1]) + (m[0] ** 2 + m[1] ** 2) * (b[1] - a[1]) + (b[0] ** 2 + b[1] ** 2) * (a[1] - m[1])) / d;
    const uy = ((a[0] ** 2 + a[1] ** 2) * (b[0] - m[0]) + (m[0] ** 2 + m[1] ** 2) * (a[0] - b[0]) + (b[0] ** 2 + b[1] ** 2) * (m[0] - a[0])) / d;
    const ang = (p: Pt3) => Math.atan2(p[1] - uy, p[0] - ux);
    const wrap = (x: number) => { while (x < 0) x += Math.PI * 2; while (x >= Math.PI * 2) x -= Math.PI * 2; return x; };
    const a0 = ang(a), am = ang(m), a1 = ang(b);
    // Sweep a→m→b CCW; if m is NOT between a and b CCW, the arc runs CW.
    const ccwToM = wrap(am - a0), ccwToB = wrap(a1 - a0);
    let theta: number;
    if (ccwToM < ccwToB) theta = wrap(a1 - a0);            // CCW sweep
    else theta = -(Math.PI * 2 - wrap(a1 - a0)) - 0;       // CW sweep: negative
    if (wrap(a1 - a0) === 0) theta = 0;
    return Math.tan(theta / 4);
}

/** Intersection of two 2D lines (infinite). Null if parallel. */
export function lineIntersection2(s1: Segment2, s2: Segment2): Pt3 | null {
    const p1 = xy(s1.a), p2 = xy(s1.b), p3 = xy(s2.a), p4 = xy(s2.b);
    const d = (p4.y - p3.y) * (p2.x - p1.x) - (p4.x - p3.x) * (p2.y - p1.y);
    if (Math.abs(d) < 1e-9) return null;
    const ua = ((p4.x - p3.x) * (p1.y - p3.y) - (p4.y - p3.y) * (p1.x - p3.x)) / d;
    const x = p1.x + ua * (p2.x - p1.x);
    const y = p1.y + ua * (p2.y - p1.y);
    const z = s1.a[2] + (s1.b[2] - s1.a[2]) * ua;
    return [x, y, z];
}

/** Infinite-line intersection WITH curve parameters: t on s1, u on s2.
 *  t/u in [0,1] means the point lies on the finite segment — the basis for
 *  real segment trimming, extending and the intersection osnap. */
export function lineParams2(s1: Segment2, s2: Segment2): { point: Pt3; t: number; u: number } | null {
    const p1 = xy(s1.a), p2 = xy(s1.b), p3 = xy(s2.a), p4 = xy(s2.b);
    const d = (p4.y - p3.y) * (p2.x - p1.x) - (p4.x - p3.x) * (p2.y - p1.y);
    if (Math.abs(d) < 1e-9) return null;
    const t = ((p4.x - p3.x) * (p1.y - p3.y) - (p4.y - p3.y) * (p1.x - p3.x)) / d;
    const u = ((p2.x - p1.x) * (p1.y - p3.y) - (p2.y - p1.y) * (p1.x - p3.x)) / d;
    const x = p1.x + t * (p2.x - p1.x);
    const y = p1.y + t * (p2.y - p1.y);
    const z = s1.a[2] + (s1.b[2] - s1.a[2]) * t;
    return { point: [x, y, z], t, u };
}

/** Intersection of two finite 2D segments. */
export function segmentIntersection2(s1: Segment2, s2: Segment2, tol = 1e-9): Pt3 | null {
    const hit = lineParams2(s1, s2);
    if (!hit) return null;
    if (hit.t < -tol || hit.t > 1 + tol || hit.u < -tol || hit.u > 1 + tol) return null;
    return hit.point;
}

/** Circle/arc-curve intersections in XY, up to two points. Arc spans are
 *  validated CCW from a0 to a1 when given (circles: pass no angles). */
export function circleIntersections2(
    c1: { center: Pt3; radius: number; a0?: number; a1?: number },
    c2: { center: Pt3; radius: number; a0?: number; a1?: number }
): Pt3[] {
    const dx = c2.center[0] - c1.center[0], dy = c2.center[1] - c1.center[1];
    const d = Math.hypot(dx, dy);
    if (d < 1e-9 || d > c1.radius + c2.radius || d < Math.abs(c1.radius - c2.radius) - 1e-9) return [];
    const a = (c1.radius * c1.radius - c2.radius * c2.radius + d * d) / (2 * d);
    const h = Math.sqrt(Math.max(0, c1.radius * c1.radius - a * a));
    const mx = c1.center[0] + a * dx / d, my = c1.center[1] + a * dy / d;
    const rx = -dy / d * h, ry = dx / d * h;
    const raw: Pt3[] = [[mx + rx, my + ry, c1.center[2]], [mx - rx, my - ry, c1.center[2]]];
    const onSpan = (p: Pt3, c: typeof c1): boolean => {
        if (c.a0 === undefined || c.a1 === undefined) return true;
        let sweep = c.a1 - c.a0;
        while (sweep <= 0) sweep += Math.PI * 2;
        let off = Math.atan2(p[1] - c.center[1], p[0] - c.center[0]) - c.a0;
        while (off < 0) off += Math.PI * 2;
        return off <= sweep + 1e-9;
    };
    const out = raw.filter((p) => onSpan(p, c1) && onSpan(p, c2));
    if (out.length === 2 && Math.hypot(out[0][0] - out[1][0], out[0][1] - out[1][1]) < 1e-9) out.pop();
    return out;
}

/** Nearest tangent point on a circle seen from an external point (null when
 *  the point is inside the circle). Picks the tangent nearest the cursor ray
 *  direction so the marker follows intent. */
export function tangentPointOnCircle(p: Pt3, center: Pt3, radius: number, viewDir?: [number, number]): Pt3 | null {
    const dx = p[0] - center[0], dy = p[1] - center[1];
    const d2 = dx * dx + dy * dy;
    const r2 = radius * radius;
    if (d2 < r2) return null;
    const a = r2 / d2, b = radius * Math.sqrt(Math.max(0, d2 - r2)) / d2;
    const t1: Pt3 = [center[0] + a * dx - b * dy, center[1] + a * dy + b * dx, center[2]];
    const t2: Pt3 = [center[0] + a * dx + b * dy, center[1] + a * dy - b * dx, center[2]];
    if (viewDir) {
        const score = (t: Pt3) => Math.hypot(t[0] - (p[0] + viewDir[0]), t[1] - (p[1] + viewDir[1]));
        return score(t1) <= score(t2) ? t1 : t2;
    }
    return t1;
}

/** Foot of perpendicular from point to segment (clamped), returns {point, t, dist}. */
export function closestOnSegment(p: Pt3, seg: Segment2): { point: Pt3; t: number; dist: number } {
    const a = xy(seg.a), b = xy(seg.b), q = xy(p);
    const ab = b.clone().sub(a);
    const len2 = ab.lengthSq();
    const t = len2 === 0 ? 0 : Math.max(0, Math.min(1, q.clone().sub(a).dot(ab) / len2));
    const foot = a.clone().addScaledVector(ab, t);
    const z = seg.a[2] + (seg.b[2] - seg.a[2]) * t;
    return { point: [foot.x, foot.y, z], t, dist: foot.distanceTo(q) };
}

/** Fillet arc between two segments sharing corner C with radius r. Returns the
 *  trim points + arc. Null when geometry impossible (parallel / too short). */
export function filletCorners(s1: Segment2, s2: Segment2, r: number): {
    p1: Pt3; p2: Pt3; center: Pt3; startAngle: number; endAngle: number;
} | null {
    const dir1 = xy(s1.b).sub(xy(s1.a)).normalize();
    const dir2 = xy(s2.a).sub(xy(s2.b)).normalize(); // pointing out of corner along s2 reversed? use:
    const in1 = xy(s1.a).sub(xy(s1.b)).normalize(); // direction away from corner along s1
    const in2 = xy(s2.b).sub(xy(s2.a)).normalize(); // away from corner along s2
    const bis = in1.add(in2);
    if (bis.lengthSq() < 1e-9) return null; // 180 deg
    bis.normalize();
    const half = Math.acos(Math.max(-1, Math.min(1, in1.dot(in2)))) / 2;
    if (half < 1e-4 || Math.PI / 2 - half < 1e-4) return null;
    const tanLen = r / Math.tan(half);
    const p1: Pt3 = [xy(s1.b).x + in1.x * tanLen, xy(s1.b).y + in1.y * tanLen, s1.b[2]];
    const p2: Pt3 = [xy(s2.a).x + in2.x * tanLen, xy(s2.a).y + in2.y * tanLen, s2.a[2]];
    const center2 = xy(s1.b).clone().addScaledVector(bis, r / Math.sin(half));
    const startAngle = Math.atan2(p1[1] - center2.y, p1[0] - center2.x);
    const endAngle = Math.atan2(p2[1] - center2.y, p2[0] - center2.x);
    void dir1; void dir2;
    return { p1, p2, center: [center2.x, center2.y, s1.b[2]], startAngle, endAngle };
}

/** Chamfer cut between two segments: trim points at d1/d2 from the corner. */
export function chamferCorners(s1: Segment2, s2: Segment2, d1: number, d2: number): { p1: Pt3; p2: Pt3 } | null {
    const in1 = xy(s1.a).sub(xy(s1.b)).normalize();
    const in2 = xy(s2.b).sub(xy(s2.a)).normalize();
    const p1: Pt3 = [xy(s1.b).x + in1.x * d1, xy(s1.b).y + in1.y * d1, s1.b[2]];
    const p2: Pt3 = [xy(s2.a).x + in2.x * d2, xy(s2.a).y + in2.y * d2, s2.a[2]];
    return { p1, p2 };
}

/** Similarity (uniform scale + rotate + translate) mapping two source points to two target points (2D). */
export function similarity2(fromA: Pt3, fromB: Pt3, toA: Pt3, toB: Pt3): (p: Pt3) => Pt3 {
    const f = new THREE.Vector2(fromB[0] - fromA[0], fromB[1] - fromA[1]);
    const t = new THREE.Vector2(toB[0] - toA[0], toB[1] - toA[1]);
    const scale = f.length() > 1e-9 ? t.length() / f.length() : 1;
    const rot = Math.atan2(t.y, t.x) - Math.atan2(f.y, f.x);
    const cos = Math.cos(rot) * scale;
    const sin = Math.sin(rot) * scale;
    return (p) => [
        toA[0] + cos * (p[0] - fromA[0]) - sin * (p[1] - fromA[1]),
        toA[1] + sin * (p[0] - fromA[0]) + cos * (p[1] - fromA[1]),
        p[2],
    ];
}

// ============================================================================
// triangulation / surfaces
// ============================================================================

/** Ear-clipping triangulation of a simple polygon (XY). Returns index triples. */
export function triangulatePolygon(points: Pt3[]): number[][] {
    const n = points.length;
    if (n < 3) return [];
    // Signed area > 0 means CCW; reverse index order otherwise so ears clip consistently.
    const signedOf = (p: Pt3[]) => { let s = 0; for (let i = 0; i < p.length; i++) { const a = p[i], b = p[(i + 1) % p.length]; s += a[0] * b[1] - b[0] * a[1]; } return s / 2; };
    const idx = points.map((_, i) => i);
    if (signedOf(points) < 0) idx.reverse();
    const triangles: number[][] = [];
    const pos = (i: number) => points[idx[i]];
    let guard = 0;
    while (idx.length > 3 && guard++ < n * n) {
        for (let i = 0; i < idx.length; i++) {
            const a = pos(i), b = pos((i + 1) % idx.length), c = pos((i + 2) % idx.length);
            const cross = (b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0]);
            if (cross <= 1e-12) continue;
            let contains = false;
            for (let j = 0; j < idx.length; j++) {
                const p = pos(j);
                if (j === i || j === (i + 1) % idx.length || j === (i + 2) % idx.length) continue;
                if (pointInTriangle2(p, a, b, c)) { contains = true; break; }
            }
            if (contains) continue;
            triangles.push([idx[(i + 1) % idx.length], idx[i], idx[(i + 2) % idx.length]]);
            idx.splice((i + 1) % idx.length, 1);
            break;
        }
    }
    if (idx.length === 3) triangles.push([idx[1], idx[0], idx[2]]);
    return triangles;
}

function pointInTriangle2(p: Pt3, a: Pt3, b: Pt3, c: Pt3): boolean {
    const d1 = (b[0] - a[0]) * (p[1] - a[1]) - (b[1] - a[1]) * (p[0] - a[0]);
    const d2 = (c[0] - b[0]) * (p[1] - b[1]) - (c[1] - b[1]) * (p[0] - b[0]);
    const d3 = (a[0] - c[0]) * (p[1] - c[1]) - (a[1] - c[1]) * (p[0] - c[0]);
    const neg = (d1 < 0 || d2 < 0 || d3 < 0), pos = (d1 > 0 || d2 > 0 || d3 > 0);
    return !(neg && pos);
}

/** Coons-style bilinear surface from 4 corner curves (arrays of equal length). */
export function coonsSurface(uCurve: Pt3[], vCurve: Pt3[]): Pt3[][] {
    if (uCurve.length < 2 || vCurve.length < 2) return [];
    const rows = vCurve.length, cols = uCurve.length;
    const c00 = uCurve[0], c01 = uCurve[cols - 1], c10 = vCurve[0], c11 = vCurve[rows - 1];
    const grid: Pt3[][] = [];
    for (let r = 0; r < rows; r++) {
        const row: Pt3[] = [];
        const v = r / (rows - 1);
        for (let c = 0; c < cols; c++) {
            const u = c / (cols - 1);
            const bottom: Pt3 = [
                c00[0] + (c01[0] - c00[0]) * u,
                c00[1] + (c01[1] - c00[1]) * u,
                c00[2] + (c01[2] - c00[2]) * u,
            ];
            const top: Pt3 = [
                c10[0] + (c11[0] - c10[0]) * u,
                c10[1] + (c11[1] - c10[1]) * u,
                c10[2] + (c11[2] - c10[2]) * u,
            ];
            const left: Pt3 = [
                c00[0] + (c10[0] - c00[0]) * v,
                c00[1] + (c10[1] - c00[1]) * v,
                c00[2] + (c10[2] - c00[2]) * v,
            ];
            const right: Pt3 = [
                c01[0] + (c11[0] - c01[0]) * v,
                c01[1] + (c11[1] - c01[1]) * v,
                c01[2] + (c11[2] - c01[2]) * v,
            ];
            const bilinear: Pt3 = [
                left[0] + (right[0] - left[0]) * u,
                left[1] + (right[1] - left[1]) * u,
                left[2] + (right[2] - left[2]) * u,
            ];
            const blend: Pt3 = [
                (1 - v) * bottom[0] + v * top[0],
                (1 - v) * bottom[1] + v * top[1],
                (1 - v) * bottom[2] + v * top[2],
            ];
            const corner: Pt3 = [
                (1 - u) * (1 - v) * c00[0] + u * (1 - v) * c01[0] + (1 - u) * v * c10[0] + u * v * c11[0],
                (1 - u) * (1 - v) * c00[1] + u * (1 - v) * c01[1] + (1 - u) * v * c10[1] + u * v * c11[1],
                (1 - u) * (1 - v) * c00[2] + u * (1 - v) * c01[2] + (1 - u) * v * c10[2] + u * v * c11[2],
            ];
            row.push([
                blend[0] + bilinear[0] - corner[0],
                blend[1] + bilinear[1] - corner[1],
                blend[2] + bilinear[2] - corner[2],
            ]);
        }
        grid.push(row);
    }
    return grid;
}

/** Skin between ordered section polylines (loft): resample all to equal count
 *  and connect consecutive sections with quad strips. */
export function loftSections(sections: Pt3[][], samplesPerSection = 24): Pt3[][] {
    const resampled = sections.map((s) => {
        const pts = s.length >= 2 ? s : [[0, 0, 0], [0, 0, 0]] as Pt3[];
        if (pts.length === 2) {
            const out: Pt3[] = [];
            for (let i = 0; i < samplesPerSection; i++) {
                const t = i / (samplesPerSection - 1);
                out.push([
                    pts[0][0] + (pts[1][0] - pts[0][0]) * t,
                    pts[0][1] + (pts[1][1] - pts[0][1]) * t,
                    pts[0][2] + (pts[1][2] - pts[0][2]) * t,
                ]);
            }
            return out;
        }
        const total = polylineLength(pts);
        const out: Pt3[] = [pts[0]];
        let carry = 0;
        const spacing = total / (samplesPerSection - 1);
        for (let i = 1; i < pts.length; i++) {
            const a = xy(pts[i - 1]), b = xy(pts[i]);
            const seg = a.distanceTo(b);
            let t = (spacing - carry) / (seg || 1);
            while (t <= 1) {
                const p = a.clone().lerp(b, t);
                out.push([p.x, p.y, pts[i - 1][2] + (pts[i][2] - pts[i - 1][2]) * t]);
                t += spacing / (seg || 1);
            }
            carry = (carry + seg) % spacing;
        }
        while (out.length < samplesPerSection) out.push(pts[pts.length - 1]);
        return out.slice(0, samplesPerSection);
    });
    return resampled;
}

/** Grid mesh data from rows x cols of points: returns vertices+triangles. */
export function gridToMesh(grid: Pt3[][]): { vertices: number[][]; indices: number[][] } {
    const vertices: number[][] = [];
    const indices: number[][] = [];
    grid.forEach((row) => row.forEach((p) => vertices.push([p[0], p[1], p[2]])));
    const cols = grid[0]?.length ?? 0;
    for (let r = 0; r < grid.length - 1; r++) {
        for (let c = 0; c < cols - 1; c++) {
            const a = r * cols + c, b = a + 1, d = a + cols, e = d + 1;
            indices.push([a, d, b], [b, d, e]);
        }
    }
    return { vertices, indices };
}

// ============================================================================
// mesh ops (wave L)
// ============================================================================

export interface MeshData { vertices: number[][]; indices: number[][] }

/** Merge vertices closer than tolerance (weld). */
export function weldMesh(mesh: MeshData, tol = 1e-4): MeshData {
    const map = new Map<string, number>();
    const vertices: number[][] = [];
    const remap = new Int32Array(mesh.vertices.length);
    mesh.vertices.forEach((v, i) => {
        const key = `${Math.round(v[0] / tol)},${Math.round(v[1] / tol)},${Math.round(v[2] / tol)}`;
        const existing = map.get(key);
        if (existing === undefined) {
            map.set(key, vertices.length);
            remap[i] = vertices.length;
            vertices.push([...v]);
        } else remap[i] = existing;
    });
    const indices = mesh.indices
        .map((t) => [remap[t[0]], remap[t[1]], remap[t[2]]].filter((x) => x !== undefined) as number[])
        .filter((t) => t.length === 3)
        .map((t) => t as number[])
        .filter((t) => !(t[0] === t[1] || t[1] === t[2] || t[0] === t[2]));
    return { vertices, indices };
}

/** Laplacian smoothing iterations over welded mesh geometry. */
export function smoothMesh(mesh: MeshData, iterations = 1, lambda = 0.5): MeshData {
    const { vertices } = mesh;
    const neighbors: Set<number>[] = vertices.map(() => new Set());
    mesh.indices.forEach(([a, b, c]) => { neighbors[a].add(b); neighbors[a].add(c); neighbors[b].add(a); neighbors[b].add(c); neighbors[c].add(a); neighbors[c].add(b); });
    let verts = vertices.map((v) => [...v]);
    for (let it = 0; it < iterations; it++) {
        const next = verts.map((v) => [...v]);
        verts.forEach((v, i) => {
            const ns = [...neighbors[i]];
            if (ns.length === 0) return;
            const avg = ns.reduce((acc, j) => [acc[0] + verts[j][0], acc[1] + verts[j][1], acc[2] + verts[j][2]], [0, 0, 0]);
            next[i] = [
                v[0] + lambda * (avg[0] / ns.length - v[0]),
                v[1] + lambda * (avg[1] / ns.length - v[1]),
                v[2] + lambda * (avg[2] / ns.length - v[2]),
            ];
        });
        verts = next;
    }
    return { vertices: verts, indices: mesh.indices };
}

/** 1→4 triangle subdivision. */
export function refineMesh(mesh: MeshData): MeshData {
    const vertices = mesh.vertices.map((v) => [...v]);
    const cache = new Map<number, number>();
    const mid = (a: number, b: number): number => {
        const key = a < b ? a * 1e6 + b : b * 1e6 + a;
        const hit = cache.get(key);
        if (hit !== undefined) return hit;
        const va = vertices[a], vb = vertices[b];
        vertices.push([(va[0] + vb[0]) / 2, (va[1] + vb[1]) / 2, (va[2] + vb[2]) / 2]);
        const idx = vertices.length - 1;
        cache.set(key, idx);
        return idx;
    };
    const indices: number[][] = [];
    mesh.indices.forEach(([a, b, c]) => {
        const ab = mid(a, b), bc = mid(b, c), ca = mid(c, a);
        indices.push([a, ab, ca], [ab, b, bc], [ca, bc, c], [ab, bc, ca]);
    });
    return { vertices, indices };
}

/** Quadric-lite edge-collapse decimation: repeatedly collapse the edge with
 *  the smallest summed triangle area into its midpoint, dropping the two
 *  incident faces. Never deletes faces outright, so no holes appear. */
export function reduceMesh(mesh: MeshData, targetRatio = 0.5): MeshData {
    const target = Math.max(4, Math.round(mesh.indices.length * Math.max(0.05, Math.min(1, targetRatio))));
    let vertices = mesh.vertices.map((v) => [...v]);
    let faces = mesh.indices.filter((t) => t.length === 3).map((t) => [...t]);
    const remap: number[] = vertices.map((_, i) => i);
    const find = (i: number): number => (remap[i] === i ? i : (remap[i] = find(remap[i])));
    while (faces.length > target) {
        // incident face areas per edge
        const edgeFaces = new Map<string, { a: number; b: number; faces: number[] }>();
        faces.forEach((t, fi) => {
            for (let e = 0; e < 3; e++) {
                const x = find(t[e]), y = find(t[(e + 1) % 3]);
                if (x === y) continue;
                const k = x < y ? `${x}:${y}` : `${y}:${x}`;
                const hit = edgeFaces.get(k);
                if (hit) hit.faces.push(fi);
                else edgeFaces.set(k, { a: x, b: y, faces: [fi] });
            }
        });
        let bestKey = '', bestCost = Infinity, bestFaces = 0;
        edgeFaces.forEach((v, k) => {
            if (v.faces.length !== 2) return; // boundary edges: collapsing makes a hole
            const cost = triArea(vertices, faces[v.faces[0]]) + triArea(vertices, faces[v.faces[1]]);
            if (cost < bestCost) { bestCost = cost; bestKey = k; bestFaces = 2; }
        });
        if (!bestKey || bestFaces !== 2) break; // no safe interior edge left
        const [a, b] = bestKey.split(':').map(Number);
        const va = vertices[a], vb = vertices[b];
        const merged: number[] = [(va[0] + vb[0]) / 2, (va[1] + vb[1]) / 2, (va[2] + (vb[2] ?? 0)) / 2];
        const drop = new Set<number>();
        edgeFaces.get(bestKey)!.faces.forEach((fi) => drop.add(fi));
        remap[b] = a;
        vertices[a] = merged;
        faces = faces
            .map((t) => t.map((i) => find(i)))
            .filter((t, fi) => !drop.has(fi) && !(t[0] === t[1] || t[1] === t[2] || t[0] === t[2]));
    }
    return { vertices, indices: faces };
}

function triArea(verts: number[][], tri: number[]): number {
    const [a, b, c] = tri;
    const va = verts[a], vb = verts[b], vc = verts[c];
    const e1 = [vb[0] - va[0], vb[1] - va[1], (vb[2] ?? 0) - (va[2] ?? 0)];
    const e2 = [vc[0] - va[0], vc[1] - va[1], (vc[2] ?? 0) - (va[2] ?? 0)];
    const cr = [e1[1] * e2[2] - e1[2] * e2[1], e1[2] * e2[0] - e1[0] * e2[2], e1[0] * e2[1] - e1[1] * e2[0]];
    return 0.5 * Math.hypot(cr[0], cr[1], cr[2]);
}

/** Mesh volume via divergence theorem (signed tetra sum). */
export function meshVolume(mesh: MeshData): number {
    let vol = 0;
    for (const [a, b, c] of mesh.indices) {
        const va = mesh.vertices[a], vb = mesh.vertices[b], vc = mesh.vertices[c];
        vol += (va[0] * (vb[1] * vc[2] - vb[2] * vc[1])
            - va[1] * (vb[0] * vc[2] - vb[2] * vc[0])
            + va[2] * (vb[0] * vc[1] - vb[1] * vc[0])) / 6;
    }
    return Math.abs(vol);
}

/** Mesh surface area (triangle sum). */
export function meshArea(mesh: MeshData): number {
    let sum = 0;
    for (const [a, b, c] of mesh.indices) {
        const va = new THREE.Vector3(...mesh.vertices[a]);
        const vb = new THREE.Vector3(...mesh.vertices[b]);
        const vc = new THREE.Vector3(...mesh.vertices[c]);
        sum += vb.clone().sub(va).cross(vc.clone().sub(va)).length() / 2;
    }
    return sum;
}

/**
 * Ordered naked boundary loops of a triangle mesh (vertex index rings).
 * An edge is naked when exactly one face uses it; adjacency walking then
 * produces closed rings. Shared by FillMeshHoles, Cap, ExtrudeMesh, Bridge.
 */
export function meshBoundaryLoops(mesh: MeshData): number[][] {
    const use = new Map<string, number>();
    const add = (a: number, b: number) => {
        const k = a < b ? `${a}:${b}` : `${b}:${a}`;
        use.set(k, (use.get(k) ?? 0) + 1);
    };
    mesh.indices.forEach(([a, b, c]) => { add(a, b); add(b, c); add(c, a); });
    const adj = new Map<number, number[]>();
    use.forEach((count, key) => {
        if (count !== 1) return;
        const [a, b] = key.split(':').map(Number);
        adj.set(a, [...(adj.get(a) ?? []), b]);
        adj.set(b, [...(adj.get(b) ?? []), a]);
    });
    const loops: number[][] = [];
    const used = new Set<number>();
    adj.forEach((_neighbors, start) => {
        if (used.has(start)) return;
        const loop: number[] = [start];
        used.add(start);
        let cur = start, prev = -1;
        for (let guard = 0; guard < 100000; guard++) {
            const next = (adj.get(cur) ?? []).find((n) => n !== prev && !used.has(n));
            if (next === undefined || next === start) break;
            loop.push(next); used.add(next); prev = cur; cur = next;
        }
        if (loop.length >= 3) loops.push(loop);
    });
    return loops;
}

/** Weld + centroid/inertia of a triangle mesh (massProp, unit density). */
export function massProperties(mesh: MeshData): { volume: number; area: number; centroid: number[] } {
    const welded = weldMesh(mesh);
    return { volume: meshVolume(welded), area: meshArea(welded), centroid: centroid(welded.vertices as Pt3[]) };
}

// ============================================================================
// deform (wave G)
// ============================================================================

/** Falloff-limited displacement of points inside radius from origin along dir. */
export function softMovePoints(points: Pt3[], center: Pt3, target: Pt3, radius: number): Pt3[] {
    const delta = new THREE.Vector3(target[0] - center[0], target[1] - center[1], target[2] - center[2]);
    return points.map((p) => {
        const d = Math.hypot(p[0] - center[0], p[1] - center[1], p[2] - center[2]);
        if (d >= radius) return [...p];
        const w = 0.5 + 0.5 * Math.cos(Math.PI * (d / radius)); // smoothstep-like
        return [p[0] + delta.x * w, p[1] + delta.y * w, p[2] + delta.z * w];
    });
}

/** Bend points along axis X by arc (angle rad, total length). */
export function bendPoints(points: Pt3[], angle: number, length: number): Pt3[] {
    const r = length / angle;
    return points.map(([x, y, z]) => {
        const a = (x / length) * angle;
        return [r * Math.sin(a), y + r * (1 - Math.cos(a)), z];
    });
}

/** Twist points about a Z axis through base by `angle` rad over height. */
export function twistPoints(points: Pt3[], base: Pt3, height: number, angle: number): Pt3[] {
    return points.map(([x, y, z]) => {
        const t = height === 0 ? 0 : (z - base[2]) / height;
        const a = angle * t;
        const dx = x - base[0], dy = y - base[1];
        return [base[0] + dx * Math.cos(a) - dy * Math.sin(a), base[1] + dx * Math.sin(a) + dy * Math.cos(a), z];
    });
}

/** Taper points: scale XY about axis by factor interpolated start→end. */
export function taperPoints(points: Pt3[], axisA: Pt3, axisB: Pt3, kStart: number, kEnd: number): Pt3[] {
    const dir = new THREE.Vector3(axisB[0] - axisA[0], axisB[1] - axisA[1], axisB[2] - axisA[2]);
    const len2 = dir.lengthSq() || 1;
    return points.map(([x, y, z]) => {
        const p = new THREE.Vector3(x - axisA[0], y - axisA[1], z - axisA[2]);
        const t = p.dot(dir) / len2;
        const k = kStart + (kEnd - kStart) * t;
        return [axisA[0] + (x - axisA[0]) * k, axisA[1] + (y - axisA[1]) * k, axisA[2] + (z - axisA[2]) * k];
    });
}

/** Shear along base line by angle. */
export function shearPoints(points: Pt3[], baseA: Pt3, baseB: Pt3, angle: number): Pt3[] {
    const d = new THREE.Vector2(baseB[0] - baseA[0], baseB[1] - baseA[1]).normalize();
    const s = Math.tan(angle);
    return points.map(([x, y, z]) => {
        const px = x - baseA[0], py = y - baseA[1];
        const h = px * d.x + py * d.y;
        const nx = -d.y, ny = d.x;
        const perp = px * nx + py * ny;
        return [baseA[0] + h * d.x + (perp + h * s) * nx, baseA[1] + h * d.y + (perp + h * s) * ny, z];
    });
}

/** Trilinear cage (bounding-box) deformation. */
export function cagePoints(points: Pt3[], min: Pt3, max: Pt3, cornerDeltas: Record<string, Pt3>): Pt3[] {
    const size = [max[0] - min[0], max[1] - min[1], max[2] - min[2]].map((s) => s || 1);
    const w = (p: Pt3): number[] => [
        (p[0] - min[0]) / size[0], (p[1] - min[1]) / size[1], (p[2] - min[2]) / size[2],
    ];
    const corners: [number, number, number][] = [
        [0, 0, 0], [1, 0, 0], [0, 1, 0], [1, 1, 0], [0, 0, 1], [1, 0, 1], [0, 1, 1], [1, 1, 1],
    ];
    const keys = corners.map((c) => `${c[0]}${c[1]}${c[2]}`);
    return points.map((p) => {
        const [u, v, t] = w(p);
        const out: Pt3 = [...p];
        for (let i = 0; i < 8; i++) {
            const cu = corners[i][0] ? u : 1 - u;
            const cv = corners[i][1] ? v : 1 - v;
            const ct = corners[i][2] ? t : 1 - t;
            const wt = cu * cv * ct;
            const d = cornerDeltas[keys[i]] ?? [0, 0, 0];
            out[0] += d[0] * wt; out[1] += d[1] * wt; out[2] += d[2] * wt;
        }
        return out;
    });
}

/** Flow: map a point along a source polyline proportionally onto a target. */
export function flowAlong(source: Pt3[], target: Pt3[]): (p: Pt3) => Pt3 {
    const srcLen = polylineLength(source) || 1;
    const tgt = resamplePolyline(target, 0.05);
    return (p) => {
        // arc-length of the closest position on the source polyline
        let bestAlong = 0, bestD = Infinity;
        let walked = 0;
        for (let i = 0; i < source.length - 1; i++) {
            const segLen = Math.hypot(source[i + 1][0] - source[i][0], source[i + 1][1] - source[i][1], source[i + 1][2] - source[i][2]);
            const c = closestOnSegment(p, { a: source[i], b: source[i + 1] });
            if (c.dist < bestD) {
                bestD = c.dist;
                bestAlong = walked + c.t * segLen;
            }
            walked += segLen;
        }
        const t = Math.max(0, Math.min(1, bestAlong / srcLen));
        const tp = tgt[Math.min(tgt.length - 1, Math.round(t * (tgt.length - 1)))] ?? target[0] ?? [0, 0, 0];
        // preserve the original height offset relative to the source start
        return [tp[0], tp[1], tp[2] + (p[2] - source[0][2])];
    };
}
